import React from 'react';
import PropTypes from 'prop-types';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { Input, Select } from '@bingads-webui-react/primitive-validation';
import { Button } from '@bingads-webui-react/primitive';
import PageContext from '@bingads-webui-static-legacy/page-context';

export const BudgetAmountEditor = ({
  i18n, errorProcessor, showPerDay, inputClassName, onKeyDown, helpId,
}) => (
  <div className="budget-amount-editor">
    <div>
      <span className="budget-label">{i18n.getString(_TL_('Budget'))}</span>
      <HelpIcon
        topic={helpId}
        ariaLabel={`${i18n.getString(_TL_('Click to learn more about budget'))}`}
      />
    </div>
    <div className="budget-input">
      {showPerDay && (
        <span
          className="shared-budget-edit-budget-message-per-day"
          aria-label={i18n.getString(_TL_('Budget amount per day'))}
          id="shared-budget-edit-budget-message-per-day"
        >
          {i18n.getString(_TL_('per day'))}
        </span>
      )}
      <Input
        {...(showPerDay ? { 'aria-labelledby': 'shared-budget-edit-budget-message-per-day' } :
        { 'aria-label': i18n.getString(_TL_('Budget amount')) })}
        className={inputClassName}
        validationProps={{ field: 'Amount', ...(errorProcessor ? { errorProcessor } : {}) }}
        onKeyDown={onKeyDown}
      />
    </div>
  </div>);

BudgetAmountEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  errorProcessor: PropTypes.func,
  showPerDay: PropTypes.bool,
  inputClassName: PropTypes.string,
  onKeyDown: PropTypes.func.isRequired,
  helpId: PropTypes.string.isRequired,
};

BudgetAmountEditor.defaultProps = {
  errorProcessor: null,
  showPerDay: true,
  inputClassName: 'inline-budget-edit-value',
};

export const BudgetMinRecommend = ({
  i18n, applyRecommend, minBudget,
}) => {
  const currencySymbol = i18n.getCurrencySymbol(PageContext.AccountCurrencyCode);
  return (
    <div className="alert alert-info budget-alert">
      <div className="alert-image" />
      <div className="alert-info-inline">
        <span>{i18n.getString(_TL_('To best optimize your campaigns, we recommend a minimum budget of '))}</span>
        <span className="bold">{currencySymbol + minBudget}</span>
        <span>{i18n.getString(_TL_('.'))}</span>
      </div>
      <div className="alert-budget-button">
        <Button
          className="apply-budget"
          onClick={applyRecommend}
        >
          {i18n.getString(_TL_('Apply recommendation'))}
        </Button>
      </div>

    </div>
  );
};

BudgetMinRecommend.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
    getCurrencySymbol: PropTypes.func,
  }).isRequired,
  applyRecommend: PropTypes.func.isRequired,
  minBudget: PropTypes.number.isRequired,
};


export const getBudgetTypes = (isDeprecateAcceleratedBudgetEnabled, selectedBudgetType) => ([
  ...(isDeprecateAcceleratedBudgetEnabled && selectedBudgetType === 'DailyStandard' ? [] : [{
    text: _TL_('Daily accelerated'),
    value: 'DailyAccelerated',
  }]), {
    text: _TL_('Daily standard'),
    value: 'DailyStandard',
  }]);

export const BudgetTypeEditor = ({
  permissions, i18n, selectedBudgetType, isDisabled, onSelect,
}) => {
  const isDeprecateAcceleratedBudgetEnabled = permissions.IsDeprecateAcceleratedBudgetEnabled;
  const budgetTypes = getBudgetTypes(isDeprecateAcceleratedBudgetEnabled, selectedBudgetType);

  return (
    <div className="budget-type-container">
      <span className="budget-type-label">{i18n.getString(_TL_('Budget type'))}</span>
      <Select
        validationProps={{ field: 'Type' }}
        value={selectedBudgetType || budgetTypes[0].value}
        className="inline-budget-edit-type"
        disabled={isDisabled}
        onSelect={onSelect}
        aria-label={i18n.getString(_TL_('Select budget type'))}
      >
        {budgetTypes.map(item => (
          <option key={`budget-type-${item.value}`} value={item.value} >{i18n.getString(item.text)}</option>
        ))}
      </Select>
    </div>);
};

BudgetTypeEditor.propTypes = {
  permissions: PropTypes.shape({
    IsDeprecateAcceleratedBudgetEnabled: PropTypes.bool.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  selectedBudgetType: PropTypes.PropTypes.oneOf([
    'DailyStandard',
    'DailyAccelerated',
  ]).isRequired,
  onSelect: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

BudgetTypeEditor.defaultProps = {
  isDisabled: false,
};
